import { useMutation } from "@apollo/client";

import {
  CREATE_OWN_OEM_ROLE_MUTATION,
  DELETE_OWN_OEM_ROLE_MUTATION,
  DUPLICATE_OWN_OEM_ROLE_MUTATION,
  UPDATE_OWN_OEM_ROLE_MUTATION,
} from "~/api";
import type {
  CreateRoleInput,
  RoleData,
  UpdateRoleInput,
} from "~/services/roles/types";
import { errorsHandler } from "~/transformers";

// Refetching by operation name only reloads lists that are on screen.
const ROLES_LIST = "listOwnOemRoles";
const USERS_LIST = "listOwnOemSupportAccounts";

const useRoleMutation = <TInput, TResult>(
  document: Parameters<typeof useMutation>[0],
  field: string,
  refetchQueries: string[],
) => {
  const [mutate, { loading }] = useMutation(document);

  const mutation = async (input: TInput): Promise<TResult> => {
    try {
      const { data } = await mutate({
        variables: { input },
        refetchQueries,
        awaitRefetchQueries: true,
      });
      return data?.[field];
    } catch (error) {
      throw errorsHandler(error);
    }
  };

  return { mutation, loading };
};

export const useCreateOwnOemRole = () =>
  useRoleMutation<CreateRoleInput, RoleData>(
    CREATE_OWN_OEM_ROLE_MUTATION,
    "createOwnOemRole",
    [ROLES_LIST],
  );

export const useUpdateOwnOemRole = () =>
  useRoleMutation<UpdateRoleInput, RoleData>(
    UPDATE_OWN_OEM_ROLE_MUTATION,
    "updateOwnOemRole",
    [ROLES_LIST],
  );

export const useDuplicateOwnOemRole = () =>
  useRoleMutation<{ _id: string; name: string }, RoleData>(
    DUPLICATE_OWN_OEM_ROLE_MUTATION,
    "duplicateOwnOemRole",
    [ROLES_LIST],
  );

/** Moving people to another role changes the users list as well. */
export const useDeleteOwnOemRole = () =>
  useRoleMutation<{ _id: string; reassignToRoleId?: string }, string>(
    DELETE_OWN_OEM_ROLE_MUTATION,
    "deleteOwnOemRole",
    [ROLES_LIST, USERS_LIST],
  );
